import { configureStore, combineReducers } from "@reduxjs/toolkit";
import userReducer from "./userSlice";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { NETWORK_TYPE } from "utils/network";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";

// Legacy blob from before the per-network split; unreachable under the new key.
storage.removeItem("persist:user");

const persistUserConfig = {
  // Namespaced per network: each build has its own Firebase project, so its own
  // uid space. A blob written by the other network's deploy must never rehydrate
  // here — and in dev both builds share localhost's origin.
  key: `user-${NETWORK_TYPE}`,
  storage,
  // cardBalance / cardTransactions are deliberately not persisted: they are
  // per-network data, and restoring them from a previous session would show
  // one network's figures under another network's label. Both are refetched by
  // react-query on mount.
  whitelist: [
    // `network` is deliberately absent: it is a build constant now, and a
    // persisted copy would override the constant on rehydrate.
    // Persisted alongside the identity it binds — a restored session must be able
    // to prove the stored card number / userType belong to the restored uid.
    "authUid",
    "userCardNumber",
    "userProfile",
    "cardInfo",
    "userType",
    "showLoadMore",
    "merchantsMap",
    // A user choice, not a build constant — and safe to restore blind because
    // useSettlementToken re-derives the effective token from the live token list.
    "settlementTokenAddress",
  ],
};

const rootReducer = combineReducers({
  user: persistReducer(persistUserConfig, userReducer),
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export default store;
